/**
 * Ручки auth, которыми пользуется трекер. Сервис общий с CRM, поэтому и
 * контракт тот же — включая две его особенности:
 *
 * — логин принимает поле `username`, хотя в нём почта;
 * — refresh читает токен из заголовка `Refresh-Token`, тело не смотрит.
 */
import { http } from '@/api/http'

import type { LoginResponse, Person, User } from './types'

/** Сколько сотрудников тянем разом: справочник VINPROP умещается в одну страницу */
const USERS_PAGE = 500

interface PersonResponse {
  id?: string
  name?: string
  email?: string
  firstName?: string
  lastName?: string
}

/**
 * Имя человека. Поле `name` в auth помечено устаревшим в пользу пары
 * `first_name`/`last_name`, но заполнено не у всех — берём что есть, иначе в
 * списке участников окажется строка без имени.
 */
const toPerson = (person: PersonResponse): Person => ({
  id: person.id ?? '',
  email: person.email ?? '',
  name: [person.firstName, person.lastName].filter(Boolean).join(' ') || person.name || '',
})

export const authApi = {
  async login(email: string, password: string): Promise<LoginResponse> {
    const { data } = await http.post<LoginResponse>('/api/v1/auth/login', {
      username: email,
      password,
    })

    return data
  },

  async logout(): Promise<void> {
    await http.post('/api/v1/auth/logout')
  },

  async me(): Promise<User> {
    const { data } = await http.get<User>('/api/v1/auth/me')

    return data
  },

  /**
   * Люди по идентификаторам — почты участников проекта. Трекер про почты не
   * знает: в `ProjectMember` у него только идентификатор, роль и имя.
   */
  async usersByIds(ids: string[]): Promise<Person[]> {
    if (!ids.length) return []

    const { data } = await http.post<{ users?: PersonResponse[] }>(
      '/api/v1/auth/users/by-ids',
      { ids },
    )

    return (data.users ?? []).map(toPerson)
  },

  /**
   * Справочник сотрудников — чтобы добавить человека в проект. Поиска по
   * подстроке у ручки нет, только страницы, поэтому берём одну большую и ищем
   * на месте. Право `users:read` есть не у всех: экран спрашивает его до
   * запроса и без него не показывает поле вовсе.
   */
  async listUsers(perPage = USERS_PAGE): Promise<Person[]> {
    const { data } = await http.get<{ users?: PersonResponse[] }>('/api/v1/auth/users', {
      params: { 'pagination.page': 1, 'pagination.perPage': perPage },
    })

    return (data.users ?? []).map(toPerson)
  },
}
